import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Crown } from 'lucide-react'
import { fetchMatches, fetchPartnerProfile, type MatchItem, type PartnerProfile } from '../lib/api'
import { openExternalLink, useTelegramBackButton } from '../lib/telegram'
import ProfileDetailSheet from '../components/ProfileDetailSheet'
import { MatchesSkeleton } from '../components/Skeleton'

export default function MatchesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [matches, setMatches] = useState<MatchItem[]>([])
  const [loading, setLoading] = useState(true)
  const [openMatch, setOpenMatch] = useState<{ match: MatchItem; profile: PartnerProfile } | null>(null)

  async function load() {
    try {
      const data = await fetchMatches()
      setMatches(data)
    } catch {
      // non-fatal — keep showing stale list
    } finally {
      setLoading(false)
    }
  }

  // Load once on mount, then refresh whenever the WebView regains focus —
  // there's no more live-changing data to poll for (a new match arrives as
  // a bot push, not an in-app badge, now that chat is retired; see
  // docs/bot-migration-decision.md's "Phase 1.5 — Chat retirement").
  useEffect(() => {
    load()
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  // A match notification's deep link lands here as ?open=<id> (see
  // App.tsx's match_<id> start_param handling) — open that match's profile
  // sheet directly instead of leaving the user to find it in the list.
  useEffect(() => {
    const openId = searchParams.get('open')
    if (!openId || matches.length === 0) return
    const match = matches.find((m) => String(m.id) === openId)
    if (!match) return
    fetchPartnerProfile(match.id)
      .then((profile) => setOpenMatch({ match, profile }))
      .catch(() => {})
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('open')
      return next
    }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches])

  function openProfile(match: MatchItem) {
    fetchPartnerProfile(match.id)
      .then((profile) => setOpenMatch({ match, profile }))
      .catch(() => {})
  }

  useTelegramBackButton(() => {
    if (openMatch) {
      setOpenMatch(null)
      return
    }
    navigate('/swipe')
  })

  return (
    <div className="flex min-h-dvh flex-col bg-tg-bg">
      <div className="flex items-center gap-3 px-4 py-4 border-b border-tg-hint/20">
        <button onClick={() => navigate('/swipe')} className="text-tg-hint text-sm">
          ← {t('common.back')}
        </button>
        <h1 className="text-lg font-semibold text-tg-text">{t('matches.title')}</h1>
      </div>

      {loading ? (
        <MatchesSkeleton />
      ) : matches.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-8 text-center">
          <span className="text-4xl">💔</span>
          <p className="text-tg-text font-medium">{t('matches.noMatchesYet')}</p>
          <p className="text-sm text-tg-hint">{t('matches.keepSwiping')}</p>
        </div>
      ) : (
        <ul className="flex-1">
          {matches.map((match) => (
            <li key={match.id}>
              <div className="flex w-full items-center gap-3 px-4 py-3">
                <button
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  onClick={() => openProfile(match)}
                >
                  {match.partner.photo_url ? (
                    <img
                      src={match.partner.photo_url}
                      alt={match.partner.display_name}
                      className="h-12 w-12 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary font-semibold text-lg">
                      {match.partner.display_name[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1 font-medium text-tg-text truncate">
                      <span className="truncate">{match.partner.display_name}</span>
                      {match.partner.crown && (
                        <Crown
                          className="h-3.5 w-3.5 shrink-0 fill-current text-amber-400"
                          aria-label={t('swipeCard.crownBadge')}
                        />
                      )}
                    </p>
                    {!match.partner.username && (
                      <p className="truncate text-sm text-tg-hint">{t('matches.partnerNoUsername')}</p>
                    )}
                  </div>
                </button>
                {match.partner.username && (
                  <button
                    onClick={() => openExternalLink(`https://t.me/${match.partner.username}`)}
                    className="shrink-0 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-white active:opacity-70"
                  >
                    {t('matches.messageOnTelegram')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {openMatch && (
        <ProfileDetailSheet
          profile={openMatch.profile}
          onClose={() => setOpenMatch(null)}
          matchActions={{
            matchId: openMatch.match.id,
            partnerUserId: openMatch.profile.user_id,
            onGone: () => {
              setOpenMatch(null)
              load()
            },
          }}
        />
      )}
    </div>
  )
}
